'use client'

import React from 'react'

type RadioProps = React.InputHTMLAttributes<HTMLInputElement> & {
  disabled?: boolean
  isError?: boolean
  isChecked?: boolean
  children?: React.ReactNode
}

const RadioContext = React.createContext<{
  disabled?: boolean
  isError?: boolean
  isChecked?: boolean
  indeterminate?: boolean
}>({
  disabled: false,
  isError: false,
  isChecked: false,
  indeterminate: false
})

const Radio = ({
  children,
  disabled,
  isError,
  isChecked,
  ...props
}: RadioProps) => {
  const contextValue = React.useMemo(
    () => ({ disabled, isError }),
    [disabled, isError]
  )

  const disabledClassName = disabled
    ? 'cursor-not-allowed bg-neutral-gray-100'
    : 'cursor-pointer peer-checked:bg-biome-green-500 peer-checked:border-biome-green-500 hover:border-biome-green-500 hover:bg-biome-green-50'

  return (
    <RadioContext.Provider value={contextValue}>
      <label className="flex relative gap-2">
        <input
          type="radio"
          className="hidden peer"
          checked={isChecked}
          disabled={disabled}
          {...props}
        />
        <div
          className={`size-5 tablet-lg:size-6 border-2 border-neutral-gray-200 rounded-full ${disabledClassName} peer-checked:ring-0 peer-checked:[&_div]:block flex flex-col items-center justify-center`}
        >
          <div className="w-2 h-2 bg-white rounded-full hidden" />
        </div>
        <div className="flex flex-col">{children}</div>
      </label>
    </RadioContext.Provider>
  )
}

type RadioLabelProps = React.HTMLAttributes<HTMLDivElement>

const RadioLabel = ({ children, ...props }: RadioLabelProps) => {
  const context = React.useContext(RadioContext)

  if (!context) {
    throw new Error('RadioLabel must be used within a Radio component')
  }

  return (
    <div
      className="text-m-bold tablet-lg:body-sm-medium text-gray-800"
      {...props}
    >
      {children}
    </div>
  )
}

type RadioDescriptionProps = React.HTMLAttributes<HTMLDivElement>

const RadioDescription = ({ children, ...props }: RadioDescriptionProps) => {
  const context = React.useContext(RadioContext)

  if (!context) {
    throw new Error('RadioDescription must be used within a Radio component')
  }

  return (
    <div
      className="text-m-regular tablet-lg:body-sm-regular text-neutral-gray-700"
      {...props}
    >
      {children}
    </div>
  )
}

Radio.Label = RadioLabel
Radio.Description = RadioDescription

export default Radio
